'use client';

import {
    Content as SelectContent,
    Icon as SelectIcon,
    Item as SelectItem,
    ItemText as SelectItemText,
    Portal as SelectPortal,
    Root as SelectUI,
    Trigger as SelectTrigger,
    Value as SelectValue,
    Viewport as SelectViewport,
} from '@radix-ui/react-select';
import {
    Children,
    createContext,
    isValidElement,
    useContext,
    useId,
    type ComponentPropsWithoutRef,
    type ReactNode,
    type Ref,
} from 'react';
import { Label as LabelPrimitive } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Icon } from '@/shared-ui/atoms/icon';
import { SelectArrowIcon } from '@/shared-ui/icons/select-arrow';

export type SelectStatus = 'default' | 'success' | 'error';

export interface SelectRootProps {
    id?: string;
    name?: string;
    placeholder?: string;
    value?: string;
    defaultValue?: string;
    status?: SelectStatus;
    disabled?: boolean;
    required?: boolean;
    className?: string;
    width?: number | string;
    ref?: Ref<HTMLButtonElement>;
    onValueChange?: (value: string) => void;
    children: ReactNode;
}

const statusBorderClassName: Record<SelectStatus, string> = {
    default: 'border-border hover:border-foreground/40 data-[state=open]:border-ring focus-visible:border-ring',
    success: 'border-success hover:border-success/90 data-[state=open]:border-success focus-visible:border-success',
    error: 'border-destructive hover:border-destructive/90 data-[state=open]:border-destructive focus-visible:border-destructive',
};

type SelectContextValue = {
    name?: string;
    placeholder: string;
    value?: string;
    defaultValue?: string;
    status: SelectStatus;
    disabled: boolean;
    required: boolean;
    triggerRef?: Ref<HTMLButtonElement>;
    onValueChange?: (value: string) => void;
    selectId: string;
    helperTextId: string;
    hasHelper: boolean;
};

const SelectContext = createContext<SelectContextValue | null>(null);
const SelectItemsContext = createContext(false);

function useSelectContext(component: string): SelectContextValue {
    const context = useContext(SelectContext);
    if (!context) {
        throw new Error(`Select.${component} must be used within Select.Root`);
    }
    return context;
}

function useSelectItemsContext(component: string): void {
    const inside = useContext(SelectItemsContext);
    if (!inside) {
        throw new Error(`Select.${component} must be used within Select.Content`);
    }
}

function isSelectHelperChild(child: ReactNode): boolean {
    if (!isValidElement(child)) {
        return false;
    }

    const childType = child.type as { displayName?: string };
    return childType.displayName === 'Select.Helper';
}

const Root = ({
    id,
    name,
    placeholder = 'Input Text',
    value,
    defaultValue,
    status = 'default',
    disabled = false,
    required = false,
    className,
    width,
    ref,
    onValueChange,
    children,
}: SelectRootProps) => {
    const generatedId = useId();
    const selectId = id ?? generatedId;
    const helperTextId = `${selectId}-helper-text`;
    const hasHelper = Children.toArray(children).some(isSelectHelperChild);

    const containerWidth = typeof width === 'number' ? `${width}px` : width;
    const containerStyle = containerWidth ? { width: containerWidth } : { width: '100%' };
    const contextValue: SelectContextValue = {
        name,
        placeholder,
        value,
        defaultValue,
        status,
        disabled,
        required,
        triggerRef: ref,
        onValueChange,
        selectId,
        helperTextId,
        hasHelper,
    };

    return (
        <div className={className} style={containerStyle}>
            <SelectUI
                defaultValue={defaultValue}
                disabled={disabled}
                name={name}
                required={required}
                value={value}
                onValueChange={onValueChange}>
                <SelectContext.Provider value={contextValue}>{children}</SelectContext.Provider>
            </SelectUI>
        </div>
    );
};

Root.displayName = 'Select.Root';

export interface SelectTriggerProps extends ComponentPropsWithoutRef<typeof SelectTrigger> {
    children?: ReactNode;
}

const Trigger = ({ className, children, ...props }: SelectTriggerProps) => {
    const { disabled, hasHelper, helperTextId, required, selectId, status, triggerRef } = useSelectContext('Trigger');

    return (
        <SelectTrigger
            aria-describedby={hasHelper ? helperTextId : undefined}
            aria-invalid={status === 'error'}
            aria-required={required}
            className={cn(
                'text-foreground bg-background flex h-[48px] w-full items-center justify-between border px-4 text-left text-base font-light shadow-none outline-none transition-colors',
                statusBorderClassName[status],
                disabled && 'border-input bg-input text-muted-foreground cursor-not-allowed hover:border-input',
                className
            )}
            id={selectId}
            ref={triggerRef}
            {...props}>
            {children}
        </SelectTrigger>
    );
};

Trigger.displayName = 'Select.Trigger';

export interface SelectTriggerTextProps {
    className?: string;
}

const TriggerText = ({ className }: SelectTriggerTextProps) => {
    const { placeholder } = useSelectContext('TriggerText');

    return <SelectValue className={cn('min-w-0 flex-1 truncate', className)} placeholder={placeholder} />;
};

TriggerText.displayName = 'Select.TriggerText';

export interface SelectTriggerIconProps {
    className?: string;
    children?: ReactNode;
}

const TriggerIcon = ({ className, children }: SelectTriggerIconProps) => {
    const { disabled } = useSelectContext('TriggerIcon');

    return (
        <SelectIcon asChild>
            {children ?? (
                <Icon
                    className={cn('text-foreground size-5', disabled && 'text-muted-foreground', className)}
                    icon={SelectArrowIcon}
                />
            )}
        </SelectIcon>
    );
};

TriggerIcon.displayName = 'Select.TriggerIcon';

export interface SelectContentProps {
    className?: string;
    children?: ReactNode;
}

const Content = ({ className, children }: SelectContentProps) => {
    useSelectContext('Content');

    return (
        <SelectPortal>
            <SelectContent
                className={cn(
                    'bg-popover border-input z-50 min-w-[var(--radix-select-trigger-width)] overflow-hidden border shadow-md',
                    className
                )}
                position="popper">
                <SelectViewport>
                    <SelectItemsContext.Provider value={true}>{children}</SelectItemsContext.Provider>
                </SelectViewport>
            </SelectContent>
        </SelectPortal>
    );
};

Content.displayName = 'Select.Content';

export interface SelectHelperProps {
    className?: string;
    id?: string;
    children: ReactNode;
}

const Helper = ({ className, children, id }: SelectHelperProps) => {
    const { disabled, helperTextId, status } = useSelectContext('Helper');
    return (
        <p
            className={cn(
                'mt-2 text-xs font-normal tracking-[0.015rem]',
                {
                    'text-destructive': status === 'error',
                    'text-foreground': disabled,
                    'text-muted-foreground': !disabled && status !== 'error',
                },
                className
            )}
            id={id ?? helperTextId}>
            {children}
        </p>
    );
};

Helper.displayName = 'Select.Helper';

export type SelectLabelProps = Omit<ComponentPropsWithoutRef<typeof LabelPrimitive>, 'htmlFor'> & {
    children: ReactNode;
};

const Label = ({ className, children, ...props }: SelectLabelProps) => {
    const { required, selectId } = useSelectContext('Label');

    return (
        <LabelPrimitive
            className={cn('text-foreground mb-2 block text-base font-normal leading-none', className)}
            htmlFor={selectId}
            {...props}>
            {children}
            {required ? <span aria-hidden="true"> *</span> : null}
        </LabelPrimitive>
    );
};

Label.displayName = 'Select.Label';

export type SelectOptionProps = ComponentPropsWithoutRef<typeof SelectItem>;

const Option = ({ className, children, ...props }: SelectOptionProps) => {
    useSelectItemsContext('Option');
    const isTextOnlyChild = typeof children === 'string' || typeof children === 'number';

    return (
        <SelectItem
            className={cn(
                'text-foreground font-light relative flex h-10 cursor-default select-none items-center py-2 pl-4 pr-2 text-sm outline-none',
                'hover:bg-snk-cream data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [highlighted]:bg-snk-cream data-[highlighted]:text-foreground',
                className
            )}
            {...props}>
            {isTextOnlyChild ? <SelectItemText>{children}</SelectItemText> : children}
        </SelectItem>
    );
};

Option.displayName = 'Select.Option';

const Select = {
    Root,
    Label,
    Trigger,
    TriggerText,
    TriggerIcon,
    Content,
    Option,
    Helper,
} as const;

export { Select };
